import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const workSteps = [
  {
    icon: 'work-icon-01.svg',
    title: 'Step 1 - Product Strategy and Discovery',
    desc: 'We define the SaaS vision, ICP, core workflows, pricing assumptions, and success metrics, then convert them into an actionable roadmap.',
  },
  {
    icon: 'work-icon-02.svg',
    title: 'Step 2 - SaaS Architecture and Tenant Design',
    desc: 'We design multi-tenant foundations including identity, roles, data isolation, billing boundaries, observability, and integration patterns.',
  },
  {
    icon: 'work-icon-03.svg',
    title: 'Step 3 - MVP Build with Core Platform Modules',
    desc: 'We build the MVP with essential SaaS capabilities like onboarding, RBAC, audit logs, usage tracking, background jobs, and admin tooling.',
  },
  {
    icon: 'work-icon-04.svg',
    title: 'Step 4 - Launch Readiness and Controlled Rollout',
    desc: 'We harden reliability, security, and performance, then launch with CI/CD, feature flags, monitoring, and rollback plans for safer releases.',
  },
  {
    icon: 'work-icon-05.svg',
    title: 'Step 5 - Scale, Optimize, and Expand the Platform',
    desc: 'We scale throughput and tenants, optimize cost and latency, add enterprise features like SSO, and evolve the product using real usage signals.',
  },
];

const faqItems = [
  {
    question: 'What types of SaaS products do you build?',
    answer:
      'We build B2B and B2C SaaS products including workflow platforms, vertical SaaS, analytics and reporting tools, marketplaces, customer portals, and subscription platforms. We focus on products that need strong roles, auditability, integrations, and a reliable platform foundation.',
  },
  {
    question: 'Do you support multi-tenant architecture and tenant isolation options?',
    answer:
      'Yes. We implement shared-schema multi-tenancy (tenant_id), schema-per-tenant, and database-per-tenant models depending on scale, data segregation needs, and compliance expectations. We also design tenant-aware caching, background jobs, and logging to reduce cross-tenant risk.',
  },
  {
    question: 'Can you implement subscriptions, billing, and usage-based pricing?',
    answer:
      'Yes. We implement subscription plans, trials, entitlements, invoicing, and usage tracking. For usage-based pricing, we design metering events that are auditable, idempotent, and explainable so customers can trust the bill and support can verify it quickly.',
  },
  {
    question: 'Can you add enterprise features like SSO and provisioning?',
    answer:
      'Yes. We support SSO (SAML or OIDC) and can plan for SCIM provisioning where needed. We also implement admin controls, access reviews, and audit logs for sensitive actions, which are commonly required by enterprise buyers.',
  },
  {
    question: 'How do you ensure reliability and performance as we scale?',
    answer:
      'We build a scalable baseline early: background processing for slow tasks, caching with tenant-safe keys, rate limiting, structured logs and metrics, and staged releases with rollback. We also plan for database scaling patterns like read replicas, partitioning, and search indexing when needed.',
  },
  {
    question: 'Can you work on an existing SaaS codebase and improve it?',
    answer:
      'Yes. We modernize SaaS platforms by tightening tenant boundaries, improving performance bottlenecks, refactoring fragile modules, and introducing missing operational foundations like observability, automated migrations, and safer deployments.',
  },
];

const ServiceSaaSEngineering = () => {
  useEffect(() => {
    if (window.WOW) new window.WOW().init();
    if (window.initMagnetic) window.initMagnetic();
  }, []);

  return (
    <>
      {/* Start breadcrumbs section */}
      <section className="breadcrumbs">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-wrapper">
                <div className="breadcrumb-cnt">
                  <span>Services Details</span>
                  <h1>SaaS Product Engineering</h1>
                  <div className="breadcrumb-list">
                    <Link to="/">Home</Link>
                    <img src="/assets/img/content/breadcrumb-arrow.svg" alt="" />
                    Services Details
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="service-details">
        <div className="about-services sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="about-content">
                  <div className="section-title-5">
                    <h2>
                      SaaS Product <br /> Engineering
                    </h2>
                  </div>

                  <p>
                    We build SaaS products that are designed to scale from MVP to a dependable multi-tenant platform. Our focus is not only shipping features, but building the foundations that reduce risk over time: tenant isolation, strong identity and access control, billing and entitlements, reliable integrations, and observability that makes production behavior measurable.
                  </p>

                  <p>
                    Whether you are launching a new product or strengthening an existing SaaS platform, we deliver a system that can evolve without constant rewrites. That includes clean APIs, safe migrations, staged releases, and a delivery model that keeps iteration fast while maintaining reliability and security.
                  </p>

                  <ul className="about-featue">
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Multi-tenant architecture and tenant-aware design
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Subscriptions, billing, entitlements, and usage metering
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      RBAC, audit logs, and secure access controls
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      API-first platform and integration readiness
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Performance, reliability, monitoring, and incident readiness
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Enterprise roadmap: SSO, admin tooling, and governance
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className="col-lg-6 wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="service-img magnetic-item">
                  <img
                    className="img-fluid"
                    src="/assets/img/services/laptop.png"
                    alt="Service"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="how-we-work-section sec-mar">
          <div className="container">
            <div
              className="row mb-60 wow animate fadeInUp"
              data-wow-delay="200ms"
              data-wow-duration="1500ms"
            >
              <div className="col-lg-12">
                <div className="section-title">
                  <h2>How We Work</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="work-item">
            <div className="container-fluid">
              <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 gy-5 justify-content-center">
                {workSteps.map((step, idx) => (
                  <div
                    key={step.title}
                    className="col magnetic-item wow animate fadeInUp"
                    data-wow-delay={`${200 + idx * 100}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="single-work">
                      <div className="work-icon">
                        <img src={`/assets/img/content/${step.icon}`} alt="" />
                      </div>
                      <div className="work-content">
                        <h3>{step.title}</h3>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="service-sort-driscription-area sec-mar">
          <div className="container">
            <div className="row">
              <div
                className="col-lg-12 wow animate fadeInUp"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="driscription-img magnetic-item">
                  <img
                    className="img-fluid"
                    src="/assets/img/services/rectangular_image.png"
                    alt="Description"
                  />
                </div>
              </div>

              <div className="driscription-content">
                <div className="row">
                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="200ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      SaaS success depends on foundations that prevent expensive rewrites. We design tenant boundaries, data models, and permission systems early, then implement billing and entitlements in a way that remains auditable as pricing evolves. For performance, we rely on proven patterns such as background job processing, caching with tenant-safe keys, idempotent APIs, and measured release practices.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Tenant isolation patterns aligned to risk and scale
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Entitlements checks separate from authorization logic
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Observability built in: logs, metrics, traces, alerts
                      </li>
                    </ul>
                  </div>

                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="300ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      We also tailor the platform to your domain and buyer expectations. Vertical SaaS often requires configurable workflows, audit-ready actions, and robust data exports. High-volume products need strong queueing and careful database scaling. Enterprise-focused SaaS typically requires SSO, admin controls, and clear operational visibility for support teams.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        B2B workflow SaaS: approvals, SLAs, audit trails
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Data-heavy SaaS: reporting layers and export controls
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Enterprise readiness: SSO, admin tooling, support workflows
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Marketplace patterns: roles, payouts, dispute handling
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="service-faq-area sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-img magnetic-item">
                  <img
                    className="img-fluid"
                    src="/assets/img/services/faqs.png"
                    alt="FAQ"
                  />
                </div>
              </div>

              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-content-area">
                  <h2>Frequently Asked Questions</h2>

                  <div className="accordion" id="accordionExample">
                    {faqItems.map((item, index) => (
                      <div key={item.question} className="accordion-item">
                        <h6 className="accordion-header" id={`heading${index}`}>
                          <button
                            className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${index}`}
                            aria-expanded={index === 0 ? 'true' : 'false'}
                            aria-controls={`collapse${index}`}
                          >
                            {item.question}
                          </button>
                        </h6>

                        <div
                          id={`collapse${index}`}
                          className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                          aria-labelledby={`heading${index}`}
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">{item.answer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceSaaSEngineering;



